import React, { useState } from 'react';
import { Platform, View, Text, TouchableOpacity, Linking, StyleSheet } from 'react-native';
import FontFamily from '../../../../constants/fonts';
import { useTheme } from '../../../../theme/useTheme';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../../../store';
import {
  ExternalLink,
  createHomepageExternalLinkFromRedux,
  updateHomepageExternalLinkFromRedux,
  deleteHomepageExternalLinkFromRedux,
} from '../../../../store/slices/adminSlice';
import LHDataTable from '../../../../components/LHDataTable';
import { AdminColors } from '../../constants/adminColors';
import { useToast } from '../../../../components/LhToastContainer';
import AdminTag from '../../components/AdminTag';
import AdminButton from '../../components/AdminButton';
import AdminModal from '../../components/AdminModal';
import { AdminFormField, AdminTextInput } from '../../components/AdminFormField';
import RowActions from './RowActions';
import DeleteConfirmModal from './DeleteConfirmModal';
import EmptyState from './EmptyState';
import { formatUserName } from '../../../../utils/emailTouserName';
import { formatDate } from '../../../../utils/dateFormatter';

export interface LinksTabProps {
  showAdd: boolean;
  onCloseAdd: () => void;
}

const LinksTab: React.FC<LinksTabProps> = ({ showAdd, onCloseAdd }) => {
  const { colors, resolvedTheme } = useTheme();
  const isDark = resolvedTheme === 'dark' && Platform.OS !== 'web';

  const dispatch = useDispatch<AppDispatch>();
  const toast = useToast();
  const externalLinks = useSelector((state: RootState) => state.configuration.homepageConfig.externalLinks);
  const [editItem, setEditItem] = React.useState<ExternalLink | null>(null);
  const [linkTitle, setLinkTitle] = useState('');
  const [linkUrl, setLinkUrl] = useState('');
  const [sequence, setSequence] = useState('');
  const [deleteTarget, setDeleteTarget] = useState<ExternalLink | null>(null);
  const [showUnsavedWarning, setShowUnsavedWarning] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  // Validation errors
  const [titleError, setTitleError] = useState('');
  const [titleHasError, setTitleHasError] = useState(false);
  const [urlError, setUrlError] = useState('');

  React.useEffect(() => {
    if (editItem) {
      setLinkTitle(editItem.title);
      setLinkUrl(editItem.url);
      setSequence(String(editItem.sequence));
    } else if (!showAdd) {
      setLinkTitle('');
      setLinkUrl('');
      setSequence('');
    }
    // Clear errors when modal opens/closes
    setTitleError('');
    setTitleHasError(false);
    setUrlError('');
  }, [editItem, showAdd]);

  // Validation functions
  const isValidUrl = (url: string): boolean => {
    if (!url.trim()) return false;
    // Must start with http:// or https://
    if (!/^https?:\/\//i.test(url.trim())) return false;
    try {
      const urlObj = new URL(url);
      // Check for valid domain extension (.com, .org, etc.)
      const domainRegex = /\.[a-z]{2,}$/i;
      return domainRegex.test(urlObj.hostname);
    } catch {
      return false;
    }
  };

  const checkDuplicates = (): string | null => {
    const trimmedTitle = linkTitle.trim();
    const trimmedUrl = linkUrl.trim();
    
    const existingLinks = externalLinks.filter(link => 
      editItem ? link.id !== editItem.id : true
    );

    const duplicateTitle = existingLinks.find(
      link => link.title.toLowerCase() === trimmedTitle.toLowerCase()
    );
    const duplicateUrl = existingLinks.find(
      link => link.url.toLowerCase() === trimmedUrl.toLowerCase()
    );

    if (duplicateTitle && duplicateUrl) {
      return 'Title and URL Already in Use';
    }
    if (duplicateUrl) {
      const duplicateLinkTitle = duplicateUrl.title;
      return `URL Already in Use by ${duplicateLinkTitle}`;
    }
    if (duplicateTitle) {
      return 'Title Already in Use';
    }
    return null;
  };

  const validateForm = (): boolean => {
    let isValid = true;
    
    // Validate title
    if (!linkTitle.trim()) {
      setTitleError('Link Title is required.');
      isValid = false;
    } else {
      setTitleError('');
    }

    // Validate URL
    if (!linkUrl.trim()) {
      setUrlError('Link URL is required.');
      isValid = false;
    } else if (!isValidUrl(linkUrl.trim())) {
      setUrlError('Please enter a valid URL starting with https:// or http:// (e.g., https://pepsico.com).');
      isValid = false;
    } else {
      setUrlError('');
    }

    // Check for duplicates only if basic validation passes
    if (isValid) {
      const duplicateError = checkDuplicates();
      if (duplicateError) {
        if (duplicateError.includes('Title and URL')) {
          setTitleError('');
          setTitleHasError(true);
          setUrlError('Title and URL Already in Use');
        } else if (duplicateError.includes('URL')) {
          setUrlError(duplicateError);
        } else {
          setTitleError(duplicateError);
        }
        isValid = false;
      }
    }

    return isValid;
  };

  const isDirty = editItem
    ? (
      linkTitle !== editItem.title ||
      linkUrl !== editItem.url ||
      sequence !== String(editItem.sequence)
    )
    : (!!linkTitle || !!linkUrl || !!sequence);

  const doClose = () => { onCloseAdd(); setEditItem(null); setShowUnsavedWarning(false); };

  const handleClose = () => {
    if (isDirty) { setShowUnsavedWarning(true); return; }
    doClose();
  };
  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    const target = deleteTarget;
    try {
      setIsDeleting(true);
      await dispatch(deleteHomepageExternalLinkFromRedux({ id: target.id })).unwrap();
      toast.showToast({ header: 'Success', subtext: 'External Link deleted successfully.', variant: 'success' });
      setDeleteTarget(null);
    } catch (error: any) {
      toast.showToast({ header: 'Error', subtext: error || 'Failed to delete External Link. Please try again.', variant: 'error' });
    } finally {
      setIsDeleting(false);
    }
  };
  const handleSave = async () => {
    if (!validateForm()) return;

    setIsSaving(true);
    const seq = parseInt(sequence, 10) || 1;

    if (editItem) {
      const resultAction = await dispatch(updateHomepageExternalLinkFromRedux({
        id: editItem.id,
        title: linkTitle,
        url: linkUrl,
        sequence: seq,
        locType: 'WH',
      }));

      if (!updateHomepageExternalLinkFromRedux.fulfilled.match(resultAction)) {
        toast.showToast({
          header: 'Error',
          subtext: resultAction.payload || 'Failed to update link.',
          variant: 'error',
        });
        setIsSaving(false);
        return;
      }
    } else {
      const resultAction = await dispatch(createHomepageExternalLinkFromRedux({
        title: linkTitle,
        url: linkUrl,
        sequence: seq,
        locType: 'WH',
      }));

      if (!createHomepageExternalLinkFromRedux.fulfilled.match(resultAction)) {
        toast.showToast({
          header: 'Error',
          subtext: resultAction.payload || 'Failed to create External Link.',
          variant: 'error',
        });
        setIsSaving(false);
        return;
      }
    }

    setIsSaving(false);
    toast.showToast({ header: 'Success', subtext: editItem ? 'External Link updated successfully.' : 'External Link created successfully.', variant: 'success' });
    doClose();
  };
  const modalVisible = showAdd || editItem !== null;

  // Sort links by sequence
  const sortedLinks = [...externalLinks].sort((a, b) => a.sequence - b.sequence);

  const linkColumns = [
    {
      columnKey: 'sequence',
      header: 'Seq',
      // width: 50,
      renderCell: (item: ExternalLink) => (
        <AdminTag label={`#${item.sequence}`} variant="gray" small />
      ),
    },
    { columnKey: 'title', header: 'Link Title', flexGrow: 1.5 },
    {
      columnKey: 'url',
      header: 'Link URL',
      flexGrow: 2.5,
      renderCell: (item: ExternalLink) => (
        <TouchableOpacity onPress={() => Linking.openURL(item.url)} activeOpacity={0.7}>
          <Text style={{ color: isDark ? colors.brand.primary : AdminColors.blue700, fontSize: 12 }}>↗ {item.url}</Text>
        </TouchableOpacity>
      ),
    },
    {
      columnKey: 'createdBy',
      header: 'Created By',
      flexGrow: 1,
      renderCell: (item: ExternalLink) => (
        <Text style={{ fontSize: 12, color: isDark ? colors.text.secondary : AdminColors.textPrimary }}>
          {formatUserName(item.createdBy)}
        </Text>
      ),
    },
    {
      columnKey: 'createdDate',
      header: 'Created Date',
      flexGrow: 1.2,
      renderCell: (item: ExternalLink) => (
        <Text style={{ fontSize: 12, color: isDark ? colors.text.secondary : AdminColors.textPrimary }}>
          {formatDate(item.createdDate)}
        </Text>
      ),
    },
    {
      columnKey: '_actions',
      header: 'Actions',
      flexGrow: 1,
      sortable: false,
      renderCell: (item: ExternalLink) => (
        <RowActions onEdit={() => setEditItem(item)} onDelete={() => setDeleteTarget(item)} />
      ),
    },
  ];

  return (
    <View>
      {sortedLinks.length === 0 ? (
        <EmptyState />
      ) : (
        <LHDataTable
          data={sortedLinks}
          columns={linkColumns}
          tableHeader=""
          tableDescription={`Showing ${sortedLinks.length} result(s)`}
          onRowClick={() => { /* Disable row click overlay */ }}
          disableRowHighlight
        />
      )}

      <AdminModal
        visible={modalVisible}
        title={editItem ? 'Edit External Link' : 'Add New External Link'}
        onClose={handleClose}
        footer={
          <>
            <AdminButton label="Cancel" variant="secondary" onPress={doClose} disabled={isSaving} />
            <AdminButton label={editItem ? 'Save Changes' : 'Save'} variant="primary" onPress={handleSave} loading={isSaving} />
          </>
        }
      >
        {showUnsavedWarning && (
          <View
            style={[
              linksLocalStyles.warningBanner,
              isDark && {
                backgroundColor: colors.statusBackground.warning,
                borderColor: colors.status.warning,
              },
            ]}
          >
            <View style={linksLocalStyles.warningBannerContent}>
              <Text style={[linksLocalStyles.warningIcon, isDark && { color: colors.status.warning }]}>⚠</Text>
              <Text style={[linksLocalStyles.warningMessage, isDark && { color: colors.status.warning }]}>
                You have unsaved changes. To discard and close, click{' '}
                <Text style={linksLocalStyles.warningMessageBold}>Cancel</Text>.
              </Text>
            </View>
            <TouchableOpacity onPress={() => setShowUnsavedWarning(false)} style={linksLocalStyles.warningCloseBtn}>
              <Text style={[linksLocalStyles.warningCloseText, isDark && { color: colors.status.warning }]}>✕</Text>
            </TouchableOpacity>
          </View>
        )}
        <AdminFormField label="Link Title" required error={titleError}>
          <AdminTextInput
            placeholder="PepsiCo Learning Portal"
            maxLength={50}
            value={linkTitle}
            onChangeText={(text) => {
              setLinkTitle(text);
              if (titleError) setTitleError('');
              if (titleHasError) setTitleHasError(false);
            }}
            hasError={!!titleError || titleHasError}
          />
        </AdminFormField>
        <AdminFormField label="Link URL" required hint="Valid domain required (.com, .org, etc.)" error={urlError}>
          <AdminTextInput
            placeholder="https://..."
            keyboardType="url"
            value={linkUrl}
            onChangeText={(text) => {
              setLinkUrl(text);
              if (urlError) setUrlError(''); // Clear error on change
            }}
            hasError={!!urlError}
          />
        </AdminFormField>
        <AdminFormField label="Display Sequence" hint="Controls the order links appear on the homepage">
          <AdminTextInput placeholder="1" keyboardType="number-pad" value={sequence} onChangeText={setSequence} />
        </AdminFormField>
      </AdminModal>

      <DeleteConfirmModal
        visible={deleteTarget !== null}
        itemLabel={deleteTarget?.title ?? ''}
        message="Are you sure you want to delete this external link?"
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDeleteConfirm}
        loading={isDeleting}
      />
    </View>
  );
};

export default LinksTab;

const linksLocalStyles = StyleSheet.create({
  warningBanner: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    backgroundColor: '#FFF3E0',
    borderWidth: 1,
    borderColor: '#FFB74D',
    borderRadius: 6,
    marginBottom: 12,
    padding: 12,
  },
  warningBannerContent: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 8,
  },
  warningIcon: {
    fontSize: 15,
    color: '#E65100',
  },
  warningMessage: {
    flex: 1,
    fontSize: 13,
    color: '#E65100',
    lineHeight: 18,
    fontFamily: FontFamily.regular,
  },
  warningMessageBold: {
    fontWeight: '700',
    fontFamily: FontFamily.medium,
  },
  warningCloseBtn: {
    padding: 2,
    marginLeft: 8,
  },
  warningCloseText: {
    fontSize: 14,
    color: '#E65100',
    fontWeight: '700',
  },
});
